/**
 * 報價單匯出「金額」欄位的共用格式化邏輯（需求 R111）。
 *
 * 問題背景（PM 2026-08-18 回報）：
 *   「報價單匯出報價幣別為美金，無論匯出 pdf/excel，單價都被自動近位到小數點第 1 位；
 *     正確應該是要進位及顯示到小數點第 2 位。系統上顯示的是正確的，只有匯出的時候
 *     變成只顯示到小數點第 1 位。美金報價，都是無條件進位到小數第 2 位。」
 *
 * 根因：quotePdfExport.js 與 quoteExcelExport.js 各有一份 `fmt(v, d = 1)`，預設一位小數
 * 且**完全不看幣別**，金額欄呼叫時又都沒帶第二參數，所以 US$ 1.23 被輸出成 US$ 1.2。
 * 資料端（QuoteCreatePage/QuoteEditPage 的 roundPrice）與畫面端（formatPrice、
 * QuoteDetailPage 的 toFixed(2)）本來就對 USD/EUR 走兩位，錯的只有匯出層。
 *
 * 為什麼抽成共用檔（同 需求 R96 quoteRemark.js 的做法）：PDF 與 Excel 是兩條完全獨立的
 * 程式路徑，歷史上（需求 R76）只改一邊就會漂移成「PDF 對、Excel 錯」。兩邊都 import
 * 這裡的 fmtMoney()/calcBoxPrice()，幣別位數與進位規則就只有一份定義。
 *
 * PM 2026-08-18 逐字拍板：
 *   Q1 歐元是否比照：A — EUR 比照 USD 一併改成 2 位（與系統畫面一致）。
 *   Q2 其他幣別：A — TWD／HKD 維持「無條件進位到小數 1 位」不動。
 *   Q3 匯出的箱價：A — 與單價同規則，且以「單價(已進位) × 箱入數」後再無條件進位。
 *   Q4 手動改價存了 3 位以上小數：PM 回「不會有此情況」→ 不另做輸入端治本，但本檔的
 *      ceil 語意天然涵蓋（1.234 → 1.24），不會出現比實際低的四捨五入結果。
 *   Q5 舊報價單（存值只有 1 位小數，如 1.2）：B — 重新以現行公式重算 → 本檔一律先對
 *      存值套 ceilMoney(幣別位數) 再格式化，不是單純補零，故新舊資料走同一條公式。
 *   Q6 整數金額：A — 固定補足位數，USD 2 → US$ 2.00（與畫面 toFixed(2) 一致）。
 */

/** 幣別 → 金額小數位數。未列出的幣別（TWD／HKD 等）走 DEFAULT_MONEY_DECIMALS。 */
export const MONEY_DECIMALS_BY_CURRENCY = { USD: 2, EUR: 2 };

/** 預設金額小數位數（Q2=A：TWD／HKD 維持 1 位）。 */
export const DEFAULT_MONEY_DECIMALS = 1;

/**
 * 取得該幣別的金額小數位數。
 * 與 QuoteCreatePage/QuoteEditPage 的 roundPrice/formatPrice 判斷（USD || EUR → 2 位）等價。
 */
export function moneyDecimals(currency) {
  return MONEY_DECIMALS_BY_CURRENCY[currency] ?? DEFAULT_MONEY_DECIMALS;
}

/**
 * 依幣別無條件進位金額（回傳 number；無法解析時回 null）。
 *
 * 為什麼不是直接 `Math.ceil(v * 100) / 100`：浮點誤差會讓「本來就剛好」的值被多進一位。
 * 實測 16.1 * 100 === 1610.0000000000002 → Math.ceil 後變成 16.11（憑空多一分）。
 * 這裡沿用 server/utils/cost.js ceilToOneDecimal 的做法，先 toFixed(6) 把 1e-13 等級的
 * 誤差抹掉再 ceil，真正該進位的值（1.234 → 1.24）仍會進位。
 */
export function ceilMoney(value, currency) {
  if (value === null || value === undefined || value === '') return null;
  const num = parseFloat(value);
  if (!Number.isFinite(num)) return null;
  const factor = Math.pow(10, moneyDecimals(currency));
  const scaled = Number((num * factor).toFixed(6));
  return Math.ceil(scaled) / factor;
}

/**
 * 匯出用金額字串：先依幣別無條件進位，再固定補足小數位（Q6=A）。
 * 無值／非數字時回傳 '—'，與匯出層原本的 fmt() 行為一致。
 *
 * 註：minimumFractionDigits === maximumFractionDigits === 位數，且值已在上一步 ceil 到
 * 同樣位數，故 toLocaleString 的四捨五入在此是 no-op，不會把 1.24 再變回 1.2。
 */
export function fmtMoney(value, currency) {
  const ceiled = ceilMoney(value, currency);
  if (ceiled === null) return '—';
  const d = moneyDecimals(currency);
  return ceiled.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
}

/**
 * 匯出用箱價 ＝ **匯出上印出來的單價** × 箱入數，乘完依幣別無條件進位一次。
 *
 * ── 現行規則（需求 R165 Q1=A，PM 2026-09-07 拍板）
 * PM 回報逐字：「餅乾 箱價 (1) 0.72*12 = 8.64 (2) 0.82*12 = 9.84」「飲料箱價: 0.49*24 = 11.76」，
 * 並於 2026-09-07 拍板「Q1=A」（其餘 Q2~Q6 採票上建議＝全 A）。
 *   → 判準只有一條：**使用者拿匯出上印出來的單價，用計算機乘箱入數，必須等於印出的箱價。**
 *   → 因此本函式第一行先 ceilMoney(unitPrice) —— 這一步不是「多進一次位」，而是重現
 *     單價欄 fmtMoney() 印出來的那個值（兩者同源、同一個 ceilMoney），確保基準逐字一致。
 *     呼叫端直接傳 item.unitPrice（＝單價欄印的來源，需求 R140 Q2=A 讀存值不重算仍成立）。
 *   → 第二次 ceilMoney（Q2=A，票上建議）是浮點雜訊防線：0.82 × 12 === 9.839999999999998，
 *     少了它會印成 9.839999999999998。它對「已進位單價 × 整數箱入數」不會改變任何值
 *     （USD 窮舉 47,952 組，改變次數 0 —— tests/quoteBoxPriceAnswer.fixture.js [3]），
 *      不得因為「看起來像第二次進位」被刪。
 *
 * ── 沿革（不得刪除；這段是三次改判的完整脈絡）
 * ‧ 需求 R111 Q3=A（2026-08-18）：箱價＝「單價(已進位) × 箱入數」後再無條件進位。
 * ‧ 需求 R140 Q1=B（2026-08-28）：**於本票 需求 R165 被 PM 改判回 A**。當時 PM 逐字
 *   「Q1（更正前提後重問）：B＝以未進位單價算」，理由是已進位單價會把箱價墊高
 *   （B115001 1 階：HKD 每箱 +1.0、USD +0.03、EUR +0.02、TWD 0），並已明文接受
 *   「使用者拿印出的單價自乘會對不上箱價」這個代價。2026-09-07 PM 實際拿到匯出後
 *   反過來回報「對不上」無法接受 → Q1 改判 A，墊高（每箱最多 HKD 1.0／USD 0.03）
 *   成為被接受的代價，可驗算性優先。
 * ‧ 需求 R140 的另一個前提更正仍然成立、且與本次改判無關，留檔勿再誤讀：
 *   PM 逐字「（需求 R111）該任務要解決的是小數點後面的位數是 2 位，並非是要進位 2 次」——
 *   下面第二次 ceilMoney 從來就不是「第二次進位」，它是浮點防線（見上）。
 * ‧ quoteUnitPrice.js 的 resolveBoxUnitPriceBase（Q1=B 時代用來還原未進位原值）自本票起
 *   **不再被任何匯出路徑呼叫**；函式本身保留（需求 R145 fixture 仍引用），見該檔說明。
 *
 * 三筆 PM 實例（tests/quoteBoxPriceApplied.fixture.js 逐格鎖住）：
 *   B12302C  US$ 0.72 × 12 ＝ US$ 8.64/箱   （Q1=B 時代印 8.62）
 *   B12302D  US$ 0.82 × 12 ＝ US$ 9.84/箱   （Q1=B 時代印 9.79）
 *   H121061/2/3 US$ 0.49 × 24 ＝ US$ 11.76/箱（Q1=B 時代印 11.69）
 */
export function calcBoxPrice(unitPrice, pcsPerBox, currency) {
  // 需求 R165 Q1=A：基準＝印出來的單價（與單價欄 fmtMoney 同一個 ceilMoney，逐字同源）。
  const printedUnit = ceilMoney(unitPrice, currency);
  const pcs = parseFloat(pcsPerBox);
  if (printedUnit === null || !Number.isFinite(pcs) || pcs <= 0) return null;
  return ceilMoney(printedUnit * pcs, currency);
}
